import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Empty, Radio, RadioChangeEvent, Skeleton } from 'antd';
import { formatBRNumber } from '@utils/formatters';
import { Pie } from '@ant-design/plots';
import useSwr from '@hooks/useSwr';
import SessionContext from '@providers/Session/SessionContext';

import * as S from './styles';

interface TotalLinesProps {
  filters: any;
}

const TotalLines = ({ filters }: TotalLinesProps) => {
  const { t } = useTranslation('dashboard');
  const { selectedApp, isMot, theme } = useContext(SessionContext);

  const [connectedOperator, setConnectedOperator] = useState(
    isMot ? 'false' : 'true',
  );
  const [orderedData, setOrderedData] = useState();

  const routeToFindData = isMot
    ? 'MOT'
    : selectedApp?.split('_').join('').toUpperCase();

  const { data: connectedBySupplies } = useSwr(
    isMot ? `/service-proxy/inventory/MOT/dashboards/count-by-operator` : null,
    {
      method: 'post',
      data: {
        ...filters,
      },
    },
  );

  const { data: connectedByOperator } = useSwr(
    `/service-proxy/inventory/${routeToFindData}/dashboards/count-by-connected-operator`,
    {
      method: 'post',
      data: {
        ...filters,
      },
    },
  );

  const order = useCallback(
    (data: { quantity: number }[]) =>
      data?.sort(
        (a: { quantity: number }, b: { quantity: number }) =>
          a.quantity - b.quantity,
      ),
    [],
  );

  useEffect(() => {
    if (connectedOperator === 'true') {
      order(connectedByOperator);
      setOrderedData(connectedByOperator);
    } else {
      order(connectedBySupplies);
      setOrderedData(connectedBySupplies);
    }
  }, [connectedByOperator, connectedBySupplies, connectedOperator, order]);

  const pieData = useMemo(
    () =>
      // @ts-ignore
      orderedData?.map(s => {
        const operatorName = s.value === null ? 'OFFLINE' : s.value;
        return {
          type: operatorName,
          // eslint-disable-next-line radix
          value: parseInt(s.count),
        };
      }),
    [orderedData],
  );

  const config = useMemo(
    () => ({
      data: pieData,
      angleField: 'value',
      colorField: 'type',
      innerRadius: 0.6,
      meta: {
        value: {
          formatter: (value: any) => formatBRNumber(value),
        },
      },
      legend: {
        itemName: {
          style: {
            fill: theme?.colors?.text1,
            fontWeight: 600,
          },
        },
      },
      appendPadding: 8,
      label: undefined,
      interactions: [{ type: 'element-selected' }, { type: 'element-active' }],
    }),
    [pieData, theme?.colors?.text1],
  );

  const PieEl = useCallback(
    () => (
      <Pie
        style={{
          height: 280,
        }}
        {...config}
      />
    ),
    [config],
  );

  const handleTabFilterChange = useCallback(
    ({ target }: RadioChangeEvent) => setConnectedOperator(target.value),
    [],
  );

  return (
    <S.CardContainer
      loading={!pieData}
      title={
        <div className="card-title">
          {t('total-lines.title')}
          <Radio.Group
            defaultValue={isMot ? 'false' : 'true'}
            onChange={handleTabFilterChange}
          >
            {isMot && (
              <Radio.Button value="false">
                {t(`total-lines.home-operator`)}
              </Radio.Button>
            )}

            <Radio.Button value="true">
              {t(`total-lines.connected-operator`)}
            </Radio.Button>
          </Radio.Group>
        </div>
      }
      bodyStyle={{ padding: !pieData ? 24 : 4 }}
      style={{ height: '100%' }}
    >
      {pieData?.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <Skeleton loading={!pieData}>
          <PieEl />
        </Skeleton>
      )}
    </S.CardContainer>
  );
};

export default TotalLines;
